import { Link } from "react-router-dom";
import type { PropsDoCatalogo } from "./AdminCatalogo";
import { ehFerramentaDeCurso, semMarcacao, situacaoDaFerramenta } from "./AdminCatalogoUi";

/**
 * Admin › Catálogo › **Visão geral** (000262).
 *
 * ---------------------------------------------------------------------------
 * O que esta tela é
 * ---------------------------------------------------------------------------
 * A nossa versão da página "Catálogo de Ferramentas" do ClickUp — o índice de
 * tudo que a casa vende e de tudo que ela entrega dentro disso, num lugar só.
 * Dali para cá ela muda numa coisa, e a diferença é o valor: **lá a lista é
 * escrita à mão e aqui ela é DERIVADA do catálogo de verdade**.
 *
 * A página de lá diz, no rodapé, que precisa ser atualizada sempre que uma
 * ferramenta for lançada, descontinuada ou alterada, "para evitar que este
 * catálogo fique desatualizado sem que ninguém perceba". Esta não pode ficar:
 * ela lê `cards`, `produtos` e `produto_cards`, que são as tabelas que a
 * vitrine do aluno lê. Se divergir da realidade, é a realidade que mudou.
 *
 * Por isso a lista das ferramentas daqui NÃO é o espelho da de lá. Guardar as
 * duas seria repetir a cicatriz dos dois catálogos de CNAE desta casa — 377
 * códigos coincidindo, 37 com descrição diferente, cada tela mostrando um nome.
 *
 * ---------------------------------------------------------------------------
 * NÃO há o bloco «O que falta», e a conferência NÃO acabou junto
 * ---------------------------------------------------------------------------
 * Ele existiu entre 16/09 e 21/09, e saiu a pedido de Victor. O que saiu foi o
 * RESUMO numa tela só; cada pendência continua sendo dita onde ela se resolve,
 * que é onde alguém pode agir sobre ela:
 *
 *   · curso sem página do produto — a linha dele, aqui embaixo, escreve "Sem
 *     página do produto" em vez de sumir (a página é editada no hub desde
 *     25/09, 000306);
 *   · produto com um dos sete lugares vazio — a coluna `falta` da sub-aba
 *     Produtos e da página de cada produto (a cicatriz da Pós Auditoria, que é
 *     o que essa coluna existe para vigiar);
 *   · página prometendo um número que o banco não faz — a tabela de
 *     conferência da página do produto, ao lado dos dois números;
 *   · ferramenta sem dono ou sem texto — a página da ferramenta.
 *
 * Está escrito aqui, e não só no commit, porque o modo de falha de um bloco
 * removido é alguém concluir que a conferência parou junto. Ela é do BANCO —
 * `falta` e `divergencias` vêm prontos de `cat_admin_catalogo`, e o teste 84
 * continua cobrando os dois.
 *
 * ---------------------------------------------------------------------------
 * Altura
 * ---------------------------------------------------------------------------
 * Lista densa, não grade de cards. É a cicatriz do IR Pro (8.249px para 1.120px
 * com o MESMO conteúdo) e a da busca de CNAE (8.521px, com a mesma linha 50
 * vezes): o uso real desta tela é procurar uma coisa pelo nome, e para isso
 * linha de texto encontra melhor que cartão.
 */

/**
 * Quantas ferramentas a Visão mostra antes do "Ver todas".
 *
 * O número existe para a caixa delas ficar MAIS CURTA que a de cursos, que é o
 * que faz o grid igualar as duas na altura da de PRODUTOS em vez de na das 27
 * ferramentas — esticar a de produtos seria o defeito inverso do que Victor
 * pediu.
 *
 * Medido em 21/09, com 12 cursos e 27 ferramentas: as duas caixas fecham em
 * **1.658px**, e com 16 linhas sobram ~145px entre a última ferramenta e o
 * rodapé — pouco menos de duas linhas. Caberia 17; 16 é o lado seguro, e a
 * razão é que o erro não avisa: se a lista passar da altura dos produtos,
 * quem estica é a caixa DELES, e a asserção de "mesmo tamanho" continua verde
 * porque as duas seguem iguais.
 *
 * Ele não é o tamanho da caixa — é um TETO. Quem decide a altura continua sendo
 * o grid, e no dia em que a casa tiver 5 cursos as duas caixas encolhem juntas
 * sem ninguém mexer aqui.
 */
const NA_VISAO = 16;

/** Um número com o rótulo embaixo. Só existe com dado medido — zero é ruído. */
function Numero({ valor, rotulo }: { valor: number; rotulo: string }) {
  return (
    <div className="rounded-cf bg-tile px-4 py-3">
      <p className="font-display text-2xl font-bold text-titulo">{valor}</p>
      <p className="mt-0.5 text-xs text-cinza">{rotulo}</p>
    </div>
  );
}

export default function AdminCatalogoVisao({ catalogo }: PropsDoCatalogo) {
  const cursos = catalogo.cursos;
  /*
    **Os números contam FERRAMENTA, não card** (pedido de Victor, 22/09).

    `cards` é a entidade genérica do banco — tudo que aparece na tela do aluno
    — e ela tem quatro tipos. Medido em 22/09: 26 `ferramenta`, 3 `curso_bonus`,
    2 `modulo` (Minhas Notas e Secretaria, da FBC) e 2 `porta` (Aulas e o CFC de
    A a Z, que levam para fora). Os 33 juntos não respondem nenhuma pergunta que
    alguém faça: "quantas ferramentas a casa tem" é a pergunta, e ela é 26.
  */
  const ferramentas = catalogo.cards.filter((c) => c.tipo === "ferramenta");
  const ativas = ferramentas.filter((c) => situacaoDaFerramenta(c).chave === "ativa");
  const desativadas = ferramentas.filter((c) => situacaoDaFerramenta(c).chave === "descontinuada");
  /*
    A terceira situação existe e NÃO tem número próprio, porque Victor pediu
    cinco. Sem esta conta, 22 + 0 não fecha com 26 e o operador fica com uma
    subtração na cabeça — então a diferença é DITA embaixo, com letra, e some
    quando é zero (zero grande é ruído).

    **E ela é dita com NOME** desde 22/09. A primeira versão dizia só "as outras
    4 estão em construção", e Victor leu a tela e concluiu que duas delas eram o
    Tec Concursos e o CFC de A a Z — que estão ativos e sempre estiveram. O erro
    foi meu: com quatro números fechando uma conta e um quinto grupo anônimo, a
    única saída de quem lê é adivinhar QUAIS são, e adivinhar sobre catálogo dá
    errado. As duas que ele não reconheceu (Área do Candidato e Área do
    Contratante) são as do "Contrate um Contador", `escopo = 'conta'`: elas não
    pertencem a curso nenhum e por isso não aparecem na cabeça de ninguém como
    "ferramenta de um curso".
  */
  const construindo = ferramentas.filter(
    (c) => situacaoDaFerramenta(c).chave === "em-construcao",
  );

  return (
    <div className="flex flex-col gap-5" data-visao-geral>
      {/* ── Os números ───────────────────────────────────────────────────── */}
      <div className="surface rise p-5 sm:p-6">
        <h2 className="font-display text-lg font-bold text-titulo">O catálogo hoje</h2>
        <p className="mt-1 text-sm text-cinza">
          Tudo que a casa vende, e tudo que ela entrega dentro disso. Esta tela é DERIVADA do
          catálogo de verdade — o mesmo que a vitrine do aluno lê —, então ela não tem como ficar
          desatualizada sem que a realidade tenha mudado junto.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Numero valor={cursos.length} rotulo="cursos" />
          <Numero valor={catalogo.produtos.length} rotulo="produtos (SKUs)" />
          <Numero valor={ferramentas.length} rotulo="ferramentas" />
          <Numero valor={ativas.length} rotulo="ferramentas ativas" />
          <Numero valor={desativadas.length} rotulo="ferramentas desativadas" />
        </div>

        {construindo.length > 0 && (
          <p className="mt-3 text-sm text-cinza" data-em-construcao={construindo.length}>
            {construindo.length === 1 ? "A outra está" : `As outras ${construindo.length} estão`}{" "}
            <strong>em construção</strong> — o card existe e o aluno vê "em breve" no lugar da
            porta, então elas não entram em nenhum dos dois números acima
            {/* Os NOMES, e não só o número: sem eles quem lê tem de adivinhar
                quais são, e a conta fechada convida a adivinhar errado. O teto
                existe porque acima dele a frase deixa de caber numa linha e a
                lista embaixo passa a ser o lugar de procurar. */}
            {construindo.length <= 6 ? (
              <>
                : <strong>{construindo.map((c) => c.nome).join(", ")}</strong>.
              </>
            ) : (
              "."
            )}
          </p>
        )}
      </div>

      {/*
        Os dois índices LADO A LADO, numa linha só (pedido de Victor, 21/09).

        **As duas caixas têm o MESMO tamanho, e quem garante isso é o grid** —
        `items-stretch`, que é o padrão dele. Não há número de altura escrito em
        lugar nenhum: a caixa mais alta manda, e a outra acompanha. A primeira
        versão usava `items-start` e as duas ficavam na altura do próprio
        conteúdo; Victor pediu iguais, e a forma que não envelhece é esta.

        O que torna isso possível é o CORTE da lista de ferramentas (`NA_VISAO`),
        que a deixa mais curta que a de cursos — sem ele as 27 ferramentas
        mandariam na altura e esticariam a caixa dos produtos, que é o defeito
        inverso.

        `lg:` e não `md:`: abaixo de 1024px as duas colunas espremem a linha da
        ferramenta (nome, selo e a lista de cursos) a ponto de cada item virar
        quatro linhas — aí uma coluna só é mais curta que duas.
      */}
      <div className="grid gap-5 lg:grid-cols-2" data-indices>
        {/* ── Os produtos ──────────────────────────────────────────────────── */}
        <section className="surface rise overflow-hidden" role="group" aria-labelledby="titulo-produtos">
          <div className="border-b border-borda p-5 sm:p-6">
            <h3 id="titulo-produtos" className="font-display text-lg font-bold text-titulo">
              Os produtos
            </h3>
            <p className="mt-1 text-sm text-cinza">
              Um por CURSO, que é o que o aluno vê. A Escola é dois SKUs e um curso só.
            </p>
          </div>
          <ul className="divide-y divide-borda">
            {cursos.map((c) => {
              const daCasa = catalogo.produtos.filter((p) => p.curso_slug === c.slug);
              // Pessoas distintas, contadas no BANCO (000290) — a soma dos SKUs
              // contava duas vezes quem tem as duas Escolas.
              const alunos = c.alunos;
              // A contagem usa a MESMA regra da página do produto e da aba de
              // Ferramentas (`ehFerramentaDeCurso`). Até 21/09 ela era escrita
              // aqui à mão e somava os módulos da FBC — uma pós sem ferramenta
              // própria aparecia com "2 ferramenta(s)".
              const cards = catalogo.cards.filter(
                (k) => ehFerramentaDeCurso(k) && k.produtos.some((p) => p.curso_slug === c.slug),
              );
              return (
                <li key={c.slug} className="p-5 sm:px-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <Link
                      to={`/admin/catalogo/produtos?produto=${encodeURIComponent(c.slug)}`}
                      data-abrir-produto={c.slug}
                      className="font-display text-base font-bold text-link underline underline-offset-2"
                    >
                      {c.nome}
                    </Link>
                    <p className="text-xs text-cinza">
                      {alunos.toLocaleString("pt-BR")} com acesso vigente · {cards.length} ferramenta(s)
                      {daCasa.length > 1 && ` · ${daCasa.length} SKUs`}
                    </p>
                  </div>
                  {/* O resumo é a primeira frase da página do produto. Quando não
                      há página, a linha DIZ isso — em vez de sumir, que é o que
                      o olho lê como "está tudo bem". */}
                  <p className="mt-1 line-clamp-2 text-sm text-cinza">
                    {c.doc ? semMarcacao(c.doc.resumo) : "Sem página do produto."}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── As ferramentas ───────────────────────────────────────────────── */}
        {/*
          `flex flex-col` com o rodapé em `mt-auto`: a caixa é esticada pelo
          grid até a altura da de produtos, e a sobra fica ENTRE a lista e o
          "Ver todas" — que é a única posição em que o botão não some no meio do
          vazio nem fica colado na última linha.
        */}
        <section
          className="surface rise flex flex-col overflow-hidden"
          role="group"
          aria-labelledby="titulo-ferramentas"
        >
          <div className="border-b border-borda p-5 sm:p-6">
            <h3 id="titulo-ferramentas" className="font-display text-lg font-bold text-titulo">
              As ferramentas
            </h3>
            <p className="mt-1 text-sm text-cinza">
              {ferramentas.length > NA_VISAO
                ? `As ${NA_VISAO} primeiras das ${ferramentas.length}, em ordem alfabética.`
                : `As ${ferramentas.length} ferramentas e onde cada uma aparece.`}{" "}
              Card universal (as Aulas, a Comunidade) vale em todo curso sem passar pelo par
              (produto, card), e por isso não tem lista de cursos.
            </p>
          </div>
          <ul className="divide-y divide-borda">
            {ferramentas.slice(0, NA_VISAO).map((f) => {
              const s = situacaoDaFerramenta(f);
              const cursosDela = [...new Set(f.produtos.map((p) => p.curso_slug))];
              return (
                <li key={f.slug} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-4 sm:px-6">
                  <Link
                    to={`/admin/catalogo/vitrine?card=${encodeURIComponent(f.slug)}`}
                    data-abrir-ferramenta={f.slug}
                    className="font-semibold text-link underline underline-offset-2"
                  >
                    {f.nome}
                  </Link>
                  <span
                    data-situacao={s.chave}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${s.classe}`}
                  >
                    {s.rotulo}
                  </span>
                  {/* Em COLUNA (desde 21/09) a lista de cursos vai para a linha
                      de baixo sempre, e não quando couber: numa largura de meia
                      tela ela cabe em alguns itens e não em outros, e o
                      resultado é uma lista em que cada linha começa num lugar
                      diferente. `basis-full` torna o item previsível — nome e
                      selo em cima, onde ele aparece embaixo. */}
                  <span className="text-sm text-cinza lg:basis-full">
                    {f.universal
                      ? "universal — todo curso"
                      : f.escopo === "conta"
                        ? "da conta — aparece uma vez na tela inicial"
                        : cursosDela.length
                          ? cursosDela.join(", ")
                          : "nenhum curso liga esta ferramenta hoje"}
                  </span>
                  {/*
                    O DONO aparece quando existe; a falta dele NÃO vira selo.

                    A primeira versão escrevia "sem dono" em cada linha, e a
                    captura mostrou o que isso é: a mesma palavra 27 vezes, num
                    estado em que NENHUMA tem dono — ou seja um selo que não
                    separa nada, que é a cicatriz do "conte quantas vezes a mesma
                    informação aparece". A linha só acrescenta o que ela SABE;
                    quem cobra o dono é a página da ferramenta, onde ele se
                    define.
                  */}
                  {f.responsavel && (
                    <span className="text-xs text-cinza" data-dono={f.slug}>
                      {f.responsavel}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          {/* O "Ver todas" leva à aba onde elas se editam, e ele aparece SEMPRE
              — inclusive quando a lista coube inteira. Um botão que some quando
              não há corte faria a pessoa procurar pela porta justamente no dia
              em que ela não está lá; e a frase muda, porque "ver todas as 27"
              com 27 na tela seria falsa. */}
          <div className="mt-auto border-t border-borda p-4 text-center sm:px-6">
            <Link
              to="/admin/catalogo/vitrine"
              data-ver-todas
              className="btn-contorno inline-flex min-h-10 items-center text-sm"
            >
              {ferramentas.length > NA_VISAO
                ? `Ver todas as ${ferramentas.length} ferramentas`
                : "Abrir a aba Ferramentas"}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
